import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import logger from "../../shared/utils/logger";
import { Button } from "@/components/ui/button";

/**
 * GeoRestrictedGate: blocking sheet for a live location-restricted result
 * (Geo-Verification v2, feature doc 23, WS-3).
 *
 * Rendered when the login-time geo re-verification (/api/auth/geo-reverify)
 * returns an explicit geo_action "geo_blocked", i.e. the player's CURRENT
 * location classifies as a restricted region (non-US/CA, one of the 15 blocked
 * US states, or Quebec). Founder policy: GEO BLOCKS LOGIN, so this holds the
 * user on a non-dismissable blocked screen with sign-out.
 *
 * Only an explicit geo_blocked verdict raises this. A 429, a network error, or
 * an inconclusive / VPN result NEVER renders it (AuthContext fails open), so a
 * legit player (including the founder on a US VPN) is never falsely locked out.
 *
 * Mirrors DobGate.jsx: non-dismissable overlay (no close button, no backdrop /
 * Esc dismissal), dark-first, sign-out escape hatch.
 */
const GeoRestrictedGate = () => {
  const { geoBlocked, geoBlockInfo, signOut } = useAuth();

  if (!geoBlocked) return null;

  const country = geoBlockInfo?.country || null;
  const region = geoBlockInfo?.region || null;

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (e) {
      logger.error("GeoRestrictedGate sign-out failed", e);
    } finally {
      window.location.assign("/signin");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="geo-gate-title"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center text-card-foreground shadow-lg">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <svg
            className="h-8 w-8 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
            />
          </svg>
        </div>

        <h2 id="geo-gate-title" className="text-lg font-semibold">
          Cluck.us isn't available in your current location
        </h2>

        <p className="mt-3 text-sm text-muted-foreground">
          Sweepstakes play is not offered in your state or province. This is
          based on your current location and is required for legal compliance.
          If you believe this is an error, please contact support.
        </p>

        {country ? (
          <div className="mt-4 rounded-lg border border-primary/20 bg-primary/10 p-3">
            <p className="text-sm text-primary">
              <span className="font-semibold">Detected location:</span> {country}
              {region ? ` (${region})` : ""}
            </p>
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button asChild variant="outline" className="w-full">
            <a href="mailto:support@cluck.us">Contact Support</a>
          </Button>
          <Button type="button" className="w-full" onClick={handleSignOut}>
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GeoRestrictedGate;
